import { getSettings } from '../profile/ProfileStore';
import { audioKeyFor, getCachedAudio } from './audioCache';
import { decode, playBuffer } from './playBuffer';
import { isUnlocked } from './audioUnlock';
import { speechService } from './SpeechService';

/**
 * Spoken key echo (FR14): each keypress quietly speaks its letter through the speaker.
 *
 * The cheap hedge against acuity loss — roughly a day of work, and it covers much of the risk that
 * would otherwise require building full auditory scanning (§5.2). Off by default; calibration or
 * the caregiver turns it on.
 *
 * **This deliberately does not go through `SpeechService`.** Two reasons, and the second is the one
 * that would have been a slow-burning bug:
 *
 *  1. `SpeechService.speak` supersedes the utterance in flight, so echoing a keystroke would cut
 *     off the sentence the person is currently having spoken.
 *  2. It emits `UtteranceSpoken`, which feeds history, diagnostics **and** Phase 2's personal
 *     n-grams. Thirty letters a minute through that path would teach the prediction engine that
 *     this person's most frequent utterance is "e", and fill their history with single characters.
 *
 * Cache-only by design: an echo that needed the network would arrive after the next keypress, and
 * a late echo is worse than none. Letters are pre-rendered by the same pass that renders phrases.
 *
 * **A cache miss therefore stays silent for that press and renders in the background instead.** For a
 * long time it did the first half and not the second, and the consequence was that the feature had
 * never made a sound in its life: `keyEchoTexts` was exported and called by nothing, so no code path
 * could put a letter in the cache, so this function returned at the miss on every keypress — online
 * included, not only in airplane mode as the notes had it. The background render is what lets a device
 * that was offline when a caregiver ticked the box heal itself one letter at a time.
 */

/** Own AudioContext-independent abort: an echo is never cancelled, it is simply short. */
const NO_ABORT = new AbortController().signal;

/** Letters already decoded, so repeated keys cost nothing after the first. */
const decoded = new Map<string, AudioBuffer>();

/** Cache keys with a render in flight, so holding down `E` asks the network once rather than eight times. */
const filling = new Set<string>();

/**
 * Render one letter into the cache in the background.
 *
 * Deliberately not through `SpeechService.speak`: that supersedes the utterance in flight and emits
 * `UtteranceSpoken`, which are the same two reasons the whole module bypasses it. `prerender` writes
 * the cache and plays nothing, which is exactly the half we want.
 */
function fillCache(text: string, voiceId: string, cacheKey: string): void {
  if (filling.has(cacheKey)) return;
  // A render that cannot reach the network would only burn a retry and log a failure per keypress.
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return;

  const provider = speechService.chain().find((entry) => entry.id === 'azureTts');
  if (!provider?.prerender) return;

  filling.add(cacheKey);
  void provider
    .prerender(text, voiceId)
    .catch((err) => {
      console.debug('[key-echo] background render failed', err);
    })
    .finally(() => {
      filling.delete(cacheKey);
    });
}

/**
 * What a key sounds like. Space and punctuation are spoken as words; letters and digits as themselves.
 *
 * The names here must agree with the `name` fields in `src/layouts/*.json`, which are what scanning and a
 * screen reader announce — a key that says "Komma" to one and nothing to the other is worse than a key that
 * says nothing at all, because only one of the two failures is discoverable.
 *
 * Digits fall through to being spoken as themselves: Azure and the platform voice both read `3` as "drei",
 * where `?` is read as silence. That asymmetry is why the layer's punctuation is listed and its digits are not.
 */
function echoTextFor(key: string): string | null {
  if (key === ' ') return 'Leerzeichen';
  if (key === ',') return 'Komma';
  if (key === '.') return 'Punkt';
  if (key === '-') return 'Bindestrich';
  // The second layer (GEO-19, GEO-20). Silent until the layer shipped, which is what GEO-11's known limit
  // and C79 both recorded: the layer was assumed to inherit a spoken-name rule that inspected one key.
  if (key === '?') return 'Fragezeichen';
  if (key === '!') return 'Ausrufezeichen';
  if (key === ':') return 'Doppelpunkt';
  if (key === '%') return 'Prozent';
  if (key === '+') return 'Plus';
  if (key === '/') return 'Schrägstrich';
  if (key === '°') return 'Grad';
  if (key.length === 1 && /[\p{L}\p{Nd}]/u.test(key)) return key;
  return null;
}

export async function speakKeyEcho(key: string): Promise<void> {
  const settings = getSettings();
  if (!settings.keyEchoEnabled || !isUnlocked()) return;

  const text = echoTextFor(key);
  if (!text) return;

  try {
    const cacheKey = await audioKeyFor(text, settings.voiceId);

    let buffer = decoded.get(cacheKey);
    if (!buffer) {
      const bytes = await getCachedAudio(cacheKey);
      if (!bytes) {
        // Silent for this press on purpose — a letter fetched now would land after the next keypress.
        // Rendered for the next one.
        fillCache(text, settings.voiceId, cacheKey);
        return;
      }
      buffer = await decode(bytes);
      decoded.set(cacheKey, buffer);
    }

    await playBuffer(buffer, NO_ABORT, settings.keyEchoVolume);
  } catch (err) {
    // Silent failure is correct here and nowhere else in the speech stack: the letter is already
    // on screen, and a warning about an echo would be noise during typing.
    console.debug('[key-echo] skipped', err);
  }
}

/** The strings the pre-render pass needs so echo works offline. */
export function keyEchoTexts(keys: readonly string[]): string[] {
  const texts = new Set<string>();
  for (const key of keys) {
    const text = echoTextFor(key);
    if (text) texts.add(text);
  }
  return [...texts];
}

/**
 * Drop the decoded buffers.
 *
 * Not only a test seam: an `AudioBuffer` belongs to the context that decoded it and carries that
 * context's `sampleRate`, so every buffer in here is unusable the moment the context is replaced
 * (`SPK-18`). Playing one against a new context is either silence or a noise, and both are worse
 * than the miss that re-renders it.
 */
export function clearKeyEchoBuffers(): void {
  decoded.clear();
  filling.clear();
}
